import { useTranslation } from 'react-i18next'
import { StatusPill } from '../../components/BasicTable/StatusPill'
import { groupAppointmentsByFranja, appointmentStatusPillDefs, isArrivalAppointment, splitEarlyArrivals } from '../../utils/inboundAppointments'
import { formatTimeInTimezone, dateInTimezone, formatCalendarDate } from '../../utils/warehouseDates'
import { FranjaCapacityStrip, boardLocale } from './FranjaCapacityStrip'

const T = 'replenishments.inbound_appointments.board'

// One card shell for franja groups and the early section. Only a card given an
// ariaLabel becomes a named region.
const GroupCard = ({ title, aside, ariaLabel, children }) => (
  <section aria-label={ariaLabel} className="bg-white rounded-md shadow-sm border border-gray-200">
    <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
      <span className="font-medium text-gray-900">{title}</span>
      {aside}
    </div>
    {children}
  </section>
)

const ClassChip = ({ appointmentClass }) => {
  const { i18n } = useTranslation()
  const isExtemporaneous = appointmentClass === 'extemporaneous'
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isExtemporaneous ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
      }`}
    >
      {i18n.t(`replenishments.inbound_appointments.classes.${appointmentClass}`, { defaultValue: appointmentClass })}
    </span>
  )
}

// One row markup for franja groups and the early section; only the
// check-in line differs, so the caller passes it in.
const AppointmentRow = ({ appointment, checkInLabel, statusDefs, onOpenReception, openingAppointmentId }) => {
  const { i18n } = useTranslation()
  const locale = boardLocale(i18n)
  const units = Number(appointment.declared_units ?? 0).toLocaleString(locale)
  return (
    <li className="px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
      {/* store_name rides the serializer (contract §5); the id
          fallback only covers a contract-drifted row. */}
      <span className="text-sm text-gray-700">{`${i18n.t(`${T}.store`)} ${appointment.store_name ?? `#${appointment.store_id}`}`}</span>
      <button
        type="button"
        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium disabled:opacity-50"
        onClick={() => onOpenReception(appointment)}
        disabled={appointment.id === openingAppointmentId}
      >
        {`${i18n.t(`${T}.replenishment`)} #${appointment.replenishment_id}`}
      </button>
      {/* declared_boxes is nullable on the platform; an empty boxes clause reads as '19,690 piezas ·  cajas'. */}
      <span className="text-sm text-gray-500">
        {appointment.declared_boxes == null
          ? i18n.t(`${T}.units`, { units })
          : i18n.t(`${T}.units_boxes`, { units, boxes: Number(appointment.declared_boxes).toLocaleString(locale) })}
      </span>
      <ClassChip appointmentClass={appointment.appointment_class} />
      <StatusPill status={appointment.status} statuses={statusDefs} className="text-xs" />
      {checkInLabel && <span className="text-sm text-gray-500">{checkInLabel}</span>}
    </li>
  )
}

// Rows received on an earlier warehouse-local day than booked. Their
// check-in already freed this day's capacity, so they never count as llegadas.
const EarlyArrivalsSection = ({ appointments, timeZone, rowProps }) => {
  const { i18n } = useTranslation()
  const locale = boardLocale(i18n)
  const title = i18n.t(`${T}.early.title`)
  const units = appointments.reduce((sum, appointment) => sum + Number(appointment.declared_units ?? 0), 0).toLocaleString(locale)
  return (
    <GroupCard
      ariaLabel={title}
      title={title}
      aside={<span className="text-sm text-gray-500">{i18n.t(`${T}.early.summary`, { count: appointments.length, units })}</span>}
    >
      <ul className="divide-y divide-gray-100">
        {appointments.map(appointment => (
          <AppointmentRow
            key={appointment.id}
            appointment={appointment}
            checkInLabel={i18n.t(`${T}.early.received_on`, {
              date: formatCalendarDate(dateInTimezone(appointment.checked_in_at, timeZone), locale, { day: 'numeric', month: 'short' }),
              time: formatTimeInTimezone(appointment.checked_in_at, timeZone),
            })}
            {...rowProps}
          />
        ))}
      </ul>
    </GroupCard>
  )
}

// Pure presentational: the container passes onOpenReception so this component
// stays router-free (and trivially testable). openingAppointmentId disables the
// row whose flow-lookup is in flight; filtersActive switches the empty copy so
// a filtered-out board never claims the day itself is empty (eng-review D27).
export const AppointmentsBoard = ({
  appointments = [],
  franjas = [],
  franjaSummary = null,
  timeZone,
  onOpenReception,
  openingAppointmentId = null,
  filtersActive = false,
}) => {
  const { i18n } = useTranslation()
  const { onDay, early } = splitEarlyArrivals(appointments, timeZone)
  const groups = groupAppointmentsByFranja(onDay, franjas)
  const statusDefs = appointmentStatusPillDefs(i18n)
  const rowProps = { statusDefs, onOpenReception, openingAppointmentId }
  const isEmpty = groups.length === 0 && early.length === 0

  if (isEmpty && filtersActive) {
    return <div className="text-center py-12 text-gray-500">{i18n.t(`${T}.no_appointments_filtered`)}</div>
  }

  if (isEmpty) {
    return <div className="text-center py-12 text-gray-500">{i18n.t(`${T}.no_appointments_day`)}</div>
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map(group => (
        <GroupCard
          key={group.franja ? `franja-${group.franja.id}` : 'extemporaneous'}
          title={group.franja ? `${group.franja.label} · ${group.franja.starts_at} – ${group.franja.ends_at}` : i18n.t(`${T}.extemporaneous_group`)}
          aside={
            <div className="flex items-center gap-3">
              {group.franja && <FranjaCapacityStrip summary={(franjaSummary || []).find(s => s.receiving_franja_id === group.franja.id)} />}
              <span className="text-sm text-gray-500">{i18n.t(`${T}.arrivals_count`, { count: group.appointments.filter(isArrivalAppointment).length })}</span>
            </div>
          }
        >
          {group.appointments.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">{i18n.t(`${T}.no_appointments`)}</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {group.appointments.map(appointment => (
                <AppointmentRow
                  key={appointment.id}
                  appointment={appointment}
                  checkInLabel={
                    appointment.checked_in_at ? i18n.t(`${T}.checked_in_at`, { time: formatTimeInTimezone(appointment.checked_in_at, timeZone) }) : null
                  }
                  {...rowProps}
                />
              ))}
            </ul>
          )}
        </GroupCard>
      ))}
      {early.length > 0 && <EarlyArrivalsSection appointments={early} timeZone={timeZone} rowProps={rowProps} />}
    </div>
  )
}
